/**
 * LifetimeValueReport (FEAT-ANALYTICS, Monetization) — lifetime value & repeat
 * purchase. Buyer economics (repeat rate, orders/buyer, ARPPU/ARPU), an
 * orders-per-buyer distribution, and an LTV-per-acquired-user cohort heatmap
 * (cumulative revenue per user by weeks since acquisition).
 */
import React from 'react';
import {
  Stack, SimpleGrid, Box, Flex, Text, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
} from '@chakra-ui/react';
import { useReport } from './api';
import { StatCard, StatGrid, Panel, BreakdownCard, ReportFrame, fmtInt, fmtPct } from './components';
import { fmtMoney } from './txnFormat';
import { CurrencyBreakdown } from './CurrencyBreakdown';

const wk = (s) => String(s || '').replace(/ 00:00:00$/, '').slice(5);

// Blue tint scaled to the richest cell — the LTV "heat".
function cellBg(v, max) {
  if (!v || !max) return 'transparent';
  const a = Math.min(0.85, 0.08 + 0.77 * (v / max));
  return `rgba(37,99,235,${a.toFixed(3)})`;
}

function LtvCohortGrid({ cohort = [] }) {
  if (!cohort.length) return <Text fontSize="sm" color="gray.500" py={6} textAlign="center">No cohorts in range.</Text>;
  const weeks = Math.max(...cohort.map((c) => c.cells.length));
  const max = Math.max(1, ...cohort.flatMap((c) => c.cells.map((x) => Number(x.ltv_per_user || 0))));
  return (
    <TableContainer>
      <Table size="sm" variant="unstyled" sx={{ 'td,th': { px: 2, py: 1, textAlign: 'center', fontSize: '11px' } }}>
        <Thead>
          <Tr>
            <Th textAlign="left !important" color="gray.500">Acquired week</Th>
            <Th color="gray.500">Users</Th>
            {[...Array(weeks).keys()].map((w) => <Th key={w} color="gray.500">Wk {w}</Th>)}
          </Tr>
        </Thead>
        <Tbody>
          {cohort.map((c) => (
            <Tr key={c.cohort_week}>
              <Td textAlign="left !important" fontWeight="600" color="gray.700" whiteSpace="nowrap">{wk(c.cohort_week)}</Td>
              <Td color="gray.600">{fmtInt(c.size)}</Td>
              {c.cells.map((x) => {
                const v = Number(x.ltv_per_user || 0);
                return (
                  <Td key={x.week} bg={cellBg(v, max)} color={v / max > 0.55 ? 'white' : 'gray.800'} fontWeight="600" title={`Cumulative ${fmtMoney(x.cumulative_revenue)}`}>
                    {v ? fmtMoney(v) : '·'}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

export function LifetimeValueReport({ params }) {
  const { data, loading, error } = useReport('ltv', params);
  const k = data?.kpis || {};
  // Per-currency revenue (DA-BUG-0455) — money KPIs blend across currencies, so for
  // multi-currency tenants show them per currency instead.
  const byCur = (data?.revenue_by_currency || []).map((r) => ({
    ...r, revenue_per_buyer: Number(r.buyers) > 0 ? Number(r.total_revenue || 0) / Number(r.buyers) : 0,
  }));
  const multi = byCur.length > 1;
  const cur = byCur.length === 1 ? byCur[0].currency : '';
  const money = (v) => (multi ? 'By currency ↓' : fmtMoney(v, cur));
  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <Stack spacing={4}>
          <StatGrid columns={{ base: 2, md: 4 }}>
            <StatCard label="Buyers" value={fmtInt(k.buyers)} help={`${fmtInt(k.active_users)} active users`} />
            <StatCard label="Repeat rate" value={fmtPct(k.repeat_rate)} help={`${fmtInt(k.repeat_buyers)} repeat buyers`} />
            <StatCard label="Orders / buyer" value={Number(k.avg_orders_per_buyer || 0).toFixed(2)} />
            <StatCard label="Avg. order value" value={money(k.avg_order_value)} />
            <StatCard label="Revenue / buyer" value={money(k.arppu)} help="ARPPU" />
            <StatCard label="Revenue / user" value={money(k.arpu)} help="ARPU — all active users" />
            <StatCard label="Total revenue" value={money(k.total_revenue)} />
          </StatGrid>

          {multi && (
            <CurrencyBreakdown
              title="Revenue by currency"
              rows={byCur}
              cols={[
                { key: 'total_revenue', label: 'Total revenue', money: true },
                { key: 'buyers', label: 'Buyers' },
                { key: 'revenue_per_buyer', label: 'Revenue / buyer', money: true },
              ]}
            />
          )}

          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
            <BreakdownCard title="Buyers by order count" rows={data.orders_distribution} labelHeader="Orders" valueHeader="Buyers" />
            <Box gridColumn={{ lg: 'span 2' }}>
              <Panel title="Lifetime value per acquired user (cumulative, by weeks since acquisition)">
                <Text fontSize="xs" color="gray.500" mb={3}>Each row is an acquisition week; cells show cumulative revenue ÷ users in that cohort.</Text>
                <LtvCohortGrid cohort={data.cohort} />
              </Panel>
            </Box>
          </SimpleGrid>
        </Stack>
      )}
    </ReportFrame>
  );
}

export default LifetimeValueReport;
